/** Funnel / pipeline steps shown on the candidate shortlist journey strip (Dutch CRM labels). */
export type PipelineStepId =
    | 'screening'
    | 'shortlist'
    | 'voorgesteld'
    | 'negatief_klant'
    | 'positief_klant'
    | 'gesprek'
    | 'aanbod'
    | 'plaatsing';

const STEP_ORDER: PipelineStepId[] = [
    'screening',
    'shortlist',
    'voorgesteld',
    'negatief_klant',
    'positief_klant',
    'gesprek',
    'aanbod',
    'plaatsing',
];

export function normalizeApplicationStatus(raw: unknown): string {
    return String(raw ?? '').trim().toLowerCase();
}

/**
 * Sollicitatie direct via careers / apply-webhook (fingerprint in cv_summary).
 * Niet verwarren met losse webreacties zonder deze markering.
 */
export function isDirectWebsiteSollicitatie(app: { cv_summary?: string | null }): boolean {
    const t = String(app?.cv_summary ?? '').toLowerCase();
    if (t.includes('direct website apply')) return true;
    if (t.includes('website apply')) return true;
    if (t.includes('ingekomen via externe website')) return true;
    if (t.includes('externe website webhook')) return true;
    return false;
}

/** Apply-webhook (`/api/webhooks/apply`) of andere flows die `_wt_ingest` zetten. */
export function isWebsiteWebhookInboundApplication(app: {
    cv_summary?: string | null;
    cv_snapshot?: unknown;
}): boolean {
    if (isDirectWebsiteSollicitatie(app)) return true;
    const snap =
        app?.cv_snapshot && typeof app.cv_snapshot === 'object'
            ? (app.cv_snapshot as Record<string, unknown>)
            : null;
    if (snap?._wt_ingest === 'website_webhook') return true;
    return false;
}

/** Bronlabel voor UI (webhook `source`, cv_summary-regel, of source_client). */
export function websiteWebhookSourceDisplayLabel(app: {
    cv_summary?: string | null;
    cv_snapshot?: unknown;
    source_client?: { name?: string | null } | null;
}): string {
    const snap =
        app?.cv_snapshot && typeof app.cv_snapshot === 'object'
            ? (app.cv_snapshot as Record<string, unknown>)
            : null;
    const fromSnap = typeof snap?._wt_source_label === 'string' ? snap._wt_source_label.trim() : '';
    if (fromSnap) return fromSnap;
    const sc = app?.source_client?.name;
    if (sc && String(sc).trim()) return String(sc).trim();
    const sum = String(app?.cv_summary || '');
    const m = sum.match(/Bron:\s*([^\n]+)/i);
    if (m?.[1]?.trim()) return m[1].trim();
    if (isWebsiteWebhookInboundApplication(app)) return 'Website / webhook';
    return '—';
}

/** Normalize for matching talent-pool bronstatussen (wp-interim ≈ wpinterim). */
export function normalizeWebsiteChannelStatusKey(raw: unknown): string {
    return String(raw ?? '')
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, '');
}

/** Talentpool-bron / careersite-statussen (o.a. werkenbijvideoworks, WP-interim). */
export function isWebsiteTalentPoolSourceStatus(raw: unknown): boolean {
    const key = normalizeWebsiteChannelStatusKey(raw);
    if (!key) return false;
    if (key === 'werkenbijvideoworks' || key.includes('werkenbijvideoworks') || key.includes('videoworks')) return true;
    if (key === 'wpinterim' || key.startsWith('wpinterim')) return true;
    if (key === 'websitesollicitatie' || key.includes('websitesollicit')) return true;
    if (key === 'webreactie' || key.includes('webreactie')) return true;
    return false;
}

function embeddedCandidateFromApp(app: any): any | null {
    const cand = app?.candidates ?? app?.candidate;
    const c = Array.isArray(cand) ? cand[0] : cand;
    return c?.id ? c : null;
}

function profileRowsFromCandidate(c: any): any[] {
    const profiles = c?.candidate_client_profiles ?? c?.profiles;
    if (!profiles) return [];
    return Array.isArray(profiles) ? profiles : [profiles];
}

/** Reporting KPI "Website sollicitaties" — webhook, bronstatus, data/cv markers. */
export function isWebsiteChannelApplication(app: any): boolean {
    if (!app) return false;
    if (isWebsiteWebhookInboundApplication(app)) return true;
    if (isWebsiteTalentPoolSourceStatus(app?.status)) return true;

    const c = embeddedCandidateFromApp(app);
    if (c) {
        if (isWebsiteTalentPoolSourceStatus(c.status)) return true;
        if (isWebsiteTalentPoolSourceStatus((c as { source?: string }).source)) return true;
        if (isWebsiteTalentPoolSourceStatus((c as { notes?: string }).notes)) return true;
        for (const p of profileRowsFromCandidate(c)) {
            if (isWebsiteTalentPoolSourceStatus(p?.status)) return true;
            const notes = String(p?.notes || '').toLowerCase();
            if (notes.includes('externe website webhook') || notes.includes('ingekomen via externe website')) return true;
        }
    }

    const s = normalizeApplicationStatus(app?.status);
    if (s.includes('webreactie')) return true;
    if (s.includes('website') && !s.includes('shortlist')) return true;

    const blob = (
        JSON.stringify(app?.data ?? {}) +
        ' ' +
        String(app?.cv_summary || '') +
        ' ' +
        String(app?.motivation || '')
    ).toLowerCase();
    if (blob.includes('werkenbijvideoworks')) return true;
    if (blob.includes('wp-interim') || blob.includes('wp_interim') || blob.includes('wpinterim')) return true;
    if (blob.includes('direct website apply')) return true;
    if (blob.includes('website sollicit')) return true;
    if (blob.includes('ingekomen via externe website')) return true;
    if (blob.includes('externe website webhook')) return true;

    const src = String(app?.data?.source || app?.data?.applicationSource || app?.data?.origin || '').toLowerCase();
    if (
        ['career_site', 'careersite', 'tenant_site', 'public_site', 'website', 'sollicitatieformulier'].some((x) =>
            src.includes(x)
        )
    )
        return true;
    const key = String(app?.data?.careerSiteKey || app?.data?.siteKey || '').toLowerCase();
    if (key.includes('videoworks') || key.includes('wp-interim') || key.includes('wp_interim') || key.includes('wpinterim'))
        return true;
    const host = String(app?.data?.careerHostname || app?.data?.referrerHost || '').toLowerCase();
    if (host && (host.includes('videoworks') || host.includes('wp-interim') || host.includes('wpinterim'))) return true;

    return false;
}

export function websiteChannelSourceDisplayLabel(app: any): string {
    const fromWebhook = websiteWebhookSourceDisplayLabel(app);
    if (fromWebhook !== '—') return fromWebhook;

    const c = embeddedCandidateFromApp(app);
    if (c) {
        for (const p of profileRowsFromCandidate(c)) {
            if (isWebsiteTalentPoolSourceStatus(p?.status)) return String(p.status).trim();
        }
        if (isWebsiteTalentPoolSourceStatus(c.status)) return String(c.status).trim();
    }
    if (isWebsiteTalentPoolSourceStatus(app?.status)) return String(app.status).trim();

    const blob = (JSON.stringify(app?.data ?? {}) + ' ' + String(app?.cv_summary || '')).toLowerCase();
    if (blob.includes('werkenbijvideoworks')) return 'werkenbijvideoworks';
    if (blob.includes('wp-interim') || blob.includes('wp_interim') || blob.includes('wpinterim')) return 'WP-interim';
    const key = String(app?.data?.careerSiteKey || app?.data?.siteKey || '').toLowerCase();
    if (key.includes('werkenbijvideoworks') || key.includes('videoworks')) return 'werkenbijvideoworks';
    if (key.includes('wp-interim') || key.includes('wp_interim') || key.includes('wpinterim')) return 'WP-interim';
    const host = String(app?.data?.careerHostname || '').toLowerCase();
    if (host.includes('videoworks')) return 'werkenbijvideoworks';
    if (host.includes('wp-interim') || host.includes('wpinterim')) return 'WP-interim';
    return String(app?.data?.applicationSource || app?.data?.source || 'Website').trim() || 'Website';
}

/** Losse webreactie / vroege inbound (niet de expliciete site-sollicitatie-flow hierboven). */
export function isWebreactieBucket(app: { status?: string; vacancy_id?: string | null; cv_summary?: string | null }): boolean {
    if (isDirectWebsiteSollicitatie(app)) return false;
    const s = normalizeApplicationStatus(app?.status);
    if (s.includes('webreactie')) return true;
    if (s.includes('website') && !s.includes('shortlist')) return true;
    if (!app?.vacancy_id && (s === 'new' || s === 'nieuw' || s === 'applied' || s === 'sollicitatie')) return true;
    return false;
}

/** Toonlabel: Lead hoort niet als applicatiestatus; tonen als Shortlist. */
export function applicationStatusDisplay(statusRaw: unknown): string {
    const raw = String(statusRaw ?? '').trim();
    if (!raw) return '—';
    const s = normalizeApplicationStatus(statusRaw);
    if (s === 'lead') return 'Shortlist';
    return raw;
}

/** Of deze `applications.status` een plaatsing is (zelfde logica als journey/shortlist). */
export function applicationStatusImpliesPlacement(statusRaw: unknown): boolean {
    return getApplicationPrimaryStep(statusRaw) === 'plaatsing';
}

/** Single “current” pipeline bucket per application (most advanced match wins). */
export function getApplicationPrimaryStep(statusRaw: unknown): PipelineStepId {
    const s = normalizeApplicationStatus(statusRaw);
    if (!s || s === 'draft_cv') return 'screening';
    // Koppeling aan vacature: geen CRM-"Lead" op de sollicitatierij — behandel als Shortlist
    if (s === 'lead' || s === 'nieuw') return 'shortlist';
    if (/\b(plaatsing|geplaatst|hired|aangenomen|placed)\b/.test(s)) return 'plaatsing';
    if (/\b(aanbod|aanbieding|offer)\b/.test(s)) return 'aanbod';
    if (/(gesprek|interview|afspraak)/.test(s)) return 'gesprek';
    if (/(positief|goedgekeurd).*(klant|client)/.test(s)) return 'positief_klant';
    if (/(negatief|afgewezen).*(klant|client)/.test(s) || /afgewezen door (de )?klant/.test(s)) return 'negatief_klant';
    if (/(voorgesteld|voorgedragen|voorgelegd|cv.*verstuurd|cv sent)/.test(s)) return 'voorgesteld';
    if (/(shortlist|op shortlist)/.test(s)) return 'shortlist';
    return 'screening';
}

export function filterApplicationsByScope(apps: any[], scope: 'current' | 'all'): any[] {
    const noDraft = (apps || []).filter((a: any) => a?.status !== 'Draft_CV');
    if (scope === 'all') return noDraft;
    return noDraft.filter((a: any) => {
        const s = normalizeApplicationStatus(a?.status);
        if (!s) return true;
        if (/\b(rejected|afgewezen|withdrawn|ingetrokken|declined)\b/.test(s)) return false;
        if (/\b(plaatsing|geplaatst|hired|aangenomen|placed)\b/.test(s)) return false;
        return true;
    });
}

export function countByPrimaryStep(apps: any[]): Record<PipelineStepId, number> {
    const out = {} as Record<PipelineStepId, number>;
    for (const id of STEP_ORDER) out[id] = 0;
    for (const a of apps || []) {
        const step = getApplicationPrimaryStep(a?.status);
        out[step]++;
    }
    return out;
}

/** Tel historische fase-events per pipeline-stap (journey-strip). */
export function countHistoryByPipelineStep(
    history: any[],
    appsById: Map<string, { status?: string }>
): Record<PipelineStepId, number> {
    const out = {} as Record<PipelineStepId, number>;
    for (const id of STEP_ORDER) out[id] = 0;
    for (const h of history || []) {
        const app = appsById.get(String(h.application_id));
        const step = getApplicationPrimaryStep(h.stage);
        if (
            app &&
            getApplicationPrimaryStep(app.status) === step &&
            String(h.stage || '').toLowerCase() === String(app.status || '').toLowerCase()
        ) {
            continue;
        }
        out[step]++;
    }
    return out;
}

/** Huidige status-telling + historie (zonder dubbele «huidige» history-regel). */
/** Pipeline-badges in Talent Pool-lijst (kolom Pipeline). */
export const TALENT_POOL_LIST_STAGE_IDS = [
    'New',
    'Shortlist',
    'Voorgesteld',
    'Gesprek',
    'Aanbod',
    'Plaatsing',
    'Rejected',
] as const;

export type TalentPoolListStageId = (typeof TALENT_POOL_LIST_STAGE_IDS)[number];

export function mapApplicationStatusToTalentPoolListStage(statusRaw: unknown): TalentPoolListStageId {
    const s = normalizeApplicationStatus(statusRaw);
    if (/\b(rejected|afgewezen|withdrawn|declined|ingetrokken)\b/.test(s)) return 'Rejected';
    if (/\b(plaatsing|geplaatst|hired|aangenomen|placed)\b/.test(s)) return 'Plaatsing';
    if (/\b(aanbod|aanbieding|offer)\b/.test(s)) return 'Aanbod';
    if (/(gesprek|interview|afspraak)/.test(s)) return 'Gesprek';
    if (/(voorgesteld|voorgedragen|voorgelegd|cv.*verstuurd)/.test(s)) return 'Voorgesteld';
    if (/(shortlist|op shortlist)/.test(s)) return 'Shortlist';
    if (s === 'new' || s === 'nieuw' || s === 'lead' || s === 'concept' || s === 'applied' || s === 'sollicitatie') {
        return 'New';
    }
    return 'New';
}

/** Per kandidaat: huidige sollicitaties + historische fasestappen voor de lijst-pipeline. */
export function buildTalentPoolListStageCounts(
    applications: Array<{ candidate_id?: string | null; status?: string | null; id?: string | null }>,
    history: Array<{ candidate_id?: string | null; application_id?: string | null; stage?: string | null }>
): Record<string, Record<string, number>> {
    const counts: Record<string, Record<string, number>> = {};
    const appsByCandidate: Record<string, Array<{ id: string; status: string }>> = {};

    const ensure = (cid: string) => {
        if (!counts[cid]) {
            counts[cid] = {};
            for (const sid of TALENT_POOL_LIST_STAGE_IDS) counts[cid][sid] = 0;
        }
    };

    for (const a of applications || []) {
        const cid = a.candidate_id ? String(a.candidate_id) : '';
        if (!cid || !a.status) continue;
        ensure(cid);
        const sid = mapApplicationStatusToTalentPoolListStage(a.status);
        counts[cid][sid] = (counts[cid][sid] || 0) + 1;
        if (a.id) {
            if (!appsByCandidate[cid]) appsByCandidate[cid] = [];
            appsByCandidate[cid].push({ id: String(a.id), status: String(a.status) });
        }
    }

    for (const h of history || []) {
        const cid = h.candidate_id ? String(h.candidate_id) : '';
        if (!cid || !h.stage) continue;
        ensure(cid);
        const sid = mapApplicationStatusToTalentPoolListStage(h.stage);
        const apps = appsByCandidate[cid] || [];
        const isCurrentDup = apps.some(
            (app) =>
                app.id === String(h.application_id) &&
                mapApplicationStatusToTalentPoolListStage(app.status) === sid &&
                String(app.status).toLowerCase() === String(h.stage).toLowerCase()
        );
        if (isCurrentDup) continue;
        counts[cid][sid] = (counts[cid][sid] || 0) + 1;
    }

    return counts;
}

/** Vacature-regel voor pipeline-popover in Talent Pool-lijst (huidige + historische fase). */
export type TalentPoolStageVacancyRow = {
    vacancyId: string;
    title: string;
    status: string;
    vacancy_number?: string;
    applicationId: string;
    /** True wanneer deze regel uit `application_stage_history` komt (niet de huidige status). */
    fromHistory?: boolean;
};

/**
 * Per kandidaat en lijst-fase: vacatures die meetellen voor de badge (zelfde logica als
 * `buildTalentPoolListStageCounts`, inclusief historie zonder dubbele huidige status).
 */
export function buildTalentPoolListStageVacancies(
    applications: Array<{
        id?: string | null;
        candidate_id?: string | null;
        status?: string | null;
        vacancy_id?: { id: string; title?: string | null; vacancy_number?: string | null } | null;
    }>,
    history: Array<{
        candidate_id?: string | null;
        application_id?: string | null;
        stage?: string | null;
    }>
): Record<string, Partial<Record<TalentPoolListStageId, TalentPoolStageVacancyRow[]>>> {
    const out: Record<string, Partial<Record<TalentPoolListStageId, TalentPoolStageVacancyRow[]>>> = {};
    const appsByCandidate: Record<string, Array<{ id: string; status: string }>> = {};
    const appsById = new Map<string, (typeof applications)[number]>();

    const rowKey = (applicationId: string, sid: TalentPoolListStageId, fromHistory: boolean) =>
        `${applicationId}:${sid}:${fromHistory ? 'h' : 'c'}`;

    const push = (cid: string, sid: TalentPoolListStageId, row: TalentPoolStageVacancyRow) => {
        if (!out[cid]) out[cid] = {};
        if (!out[cid][sid]) out[cid][sid] = [];
        const list = out[cid][sid]!;
        const key = rowKey(row.applicationId, sid, Boolean(row.fromHistory));
        if (list.some((r) => rowKey(r.applicationId, sid, Boolean(r.fromHistory)) === key)) return;
        list.push(row);
    };

    for (const a of applications || []) {
        if (a?.id) appsById.set(String(a.id), a);
    }

    for (const a of applications || []) {
        const cid = a.candidate_id ? String(a.candidate_id) : '';
        const appId = a.id ? String(a.id) : '';
        const vac = a.vacancy_id;
        if (!cid || !a.status || !appId || !vac?.id) continue;

        const sid = mapApplicationStatusToTalentPoolListStage(a.status);
        push(cid, sid, {
            vacancyId: String(vac.id),
            title: String(vac.title || 'Vacature'),
            status: String(a.status),
            vacancy_number: vac.vacancy_number != null ? String(vac.vacancy_number) : undefined,
            applicationId: appId,
            fromHistory: false,
        });

        if (!appsByCandidate[cid]) appsByCandidate[cid] = [];
        appsByCandidate[cid].push({ id: appId, status: String(a.status) });
    }

    for (const h of history || []) {
        const appId = h.application_id ? String(h.application_id) : '';
        const app = appId ? appsById.get(appId) : undefined;
        const cid = String(h.candidate_id || app?.candidate_id || '').trim();
        const vac = app?.vacancy_id;
        if (!cid || !h.stage || !appId || !vac?.id) continue;

        const sid = mapApplicationStatusToTalentPoolListStage(h.stage);
        const apps = appsByCandidate[cid] || [];
        const isCurrentDup = apps.some(
            (entry) =>
                entry.id === appId &&
                mapApplicationStatusToTalentPoolListStage(entry.status) === sid &&
                String(entry.status).toLowerCase() === String(h.stage).toLowerCase()
        );
        if (isCurrentDup) continue;

        push(cid, sid, {
            vacancyId: String(vac.id),
            title: String(vac.title || 'Vacature'),
            status: String(h.stage),
            vacancy_number: vac.vacancy_number != null ? String(vac.vacancy_number) : undefined,
            applicationId: appId,
            fromHistory: true,
        });
    }

    return out;
}

export function talentPoolListStageVacanciesFor(
    byCandidate: Record<string, Partial<Record<TalentPoolListStageId, TalentPoolStageVacancyRow[]>>>,
    candidateId: string,
    stageId: TalentPoolListStageId
): TalentPoolStageVacancyRow[] {
    return byCandidate[candidateId]?.[stageId] || [];
}

export function mergePipelineStepCountsWithHistory(
    currentApps: any[],
    history: any[]
): Record<PipelineStepId, number> {
    const current = countByPrimaryStep(currentApps);
    const appsById = new Map<string, { status?: string }>();
    for (const a of currentApps || []) {
        if (a?.id) appsById.set(String(a.id), a);
    }
    for (const h of history || []) {
        const id = String(h.application_id || '');
        if (id && !appsById.has(id)) {
            appsById.set(id, { status: h.stage });
        }
    }
    const hist = countHistoryByPipelineStep(history, appsById);
    const merged = {} as Record<PipelineStepId, number>;
    for (const id of STEP_ORDER) {
        merged[id] = (current[id] || 0) + (hist[id] || 0);
    }
    return merged;
}

export function maxDateIso(dates: (string | null | undefined)[]): string | null {
    let best: number | null = null;
    for (const d of dates) {
        if (!d) continue;
        const t = new Date(d).getTime();
        if (Number.isNaN(t)) continue;
        if (best === null || t > best) best = t;
    }
    return best === null ? null : new Date(best).toISOString();
}

/** Accordion-fases in kandidaat-shortlist (Talent Pool). */
export type ShortlistPhaseId =
    | 'site_sollicitatie'
    | 'shortlist'
    | 'voorgesteld'
    | 'gesprek'
    | 'aanbod'
    | 'plaatsing';

/** Journey-strip stap → accordion-sectie in Shortlist-tab. */
export function pipelineStepToShortlistPhase(step: PipelineStepId): ShortlistPhaseId {
    switch (step) {
        case 'screening':
            return 'site_sollicitatie';
        case 'shortlist':
            return 'shortlist';
        case 'voorgesteld':
        case 'negatief_klant':
        case 'positief_klant':
            return 'voorgesteld';
        case 'gesprek':
            return 'gesprek';
        case 'aanbod':
            return 'aanbod';
        case 'plaatsing':
            return 'plaatsing';
        default:
            return 'shortlist';
    }
}

/** Map `application_stage_history.stage` naar shortlist-accordion-fase. */
export function historyStageToShortlistPhase(
    stageRaw: unknown,
    app?: { status?: string; cv_summary?: string | null; vacancy_id?: string | null } | null
): ShortlistPhaseId {
    const stage = String(stageRaw ?? '').trim();
    if (app && isDirectWebsiteSollicitatie(app)) return 'site_sollicitatie';

    const step = getApplicationPrimaryStep(stage);
    switch (step) {
        case 'voorgesteld':
        case 'negatief_klant':
        case 'positief_klant':
            return 'voorgesteld';
        case 'gesprek':
            return 'gesprek';
        case 'aanbod':
            return 'aanbod';
        case 'plaatsing':
            return 'plaatsing';
        case 'shortlist':
            return 'shortlist';
        default:
            return 'shortlist';
    }
}

/** Huidige accordion-fase van een sollicitatie (zelfde buckets als shortlist-UI). */
export function applicationToShortlistPhase(app: {
    status?: string;
    cv_summary?: string | null;
    vacancy_id?: string | null;
}): ShortlistPhaseId {
    if (isDirectWebsiteSollicitatie(app)) return 'site_sollicitatie';
    const step = getApplicationPrimaryStep(app?.status);
    switch (step) {
        case 'voorgesteld':
        case 'negatief_klant':
        case 'positief_klant':
            return 'voorgesteld';
        case 'gesprek':
            return 'gesprek';
        case 'aanbod':
            return 'aanbod';
        case 'plaatsing':
            return 'plaatsing';
        case 'shortlist':
            return 'shortlist';
        default:
            return 'shortlist';
    }
}
